// Gameplay entities of a map: spawn points, triggers, teleporters, jump pads,
// hurt zones, ETJump timerun entities, use buttons (+activate) and the static
// description of movers.
// Shared by the client and the server, which run the same logic. Everything
// that changes while playing (mover state, trigger cooldowns) lives in the
// player's session.

import { allowTeams, entityAngles, entityNum, entityVec, inlineModel, parseEntities, type Entity } from '../entities';
import type { Vec3 } from '../math';
import { f } from '../f32';
import { CONTENTS_BODY, CONTENTS_CORPSE, CONTENTS_MISSILECLIP, CONTENTS_SOLID } from '../surfaceflags';
import { DEFAULT_RULES, type MapRules } from '../physics/pmove';
import type { PhysicsWorld } from '../world';
import { hiddenEntities, scriptStart } from './mapScript';
import { MOVER_CLASSES, MoverSet, createMoverDef, type MoverDef } from './movers';

/** Brush entities that block the player at their spawn position (movers: see movers.ts). */
const SOLID_CLASSES = new Set(['func_static', 'func_explosive', 'func_fakebrush', 'func_brushmodel']);

const isTimerClass = (cls: string) => cls === 'target_checkpoint' || cls === 'trigger_checkpoint' || cls === 'trigger_checkpoint_ext' || cls.includes('starttimer') || cls.includes('stoptimer');

/** Timerun names in spawn order, like MapLogic.runs, without building the map's collision. */
export function timerunNames(entities: Entity[]): string[] {
  const names = new Set<string>();
  for (const e of entities) if (isTimerClass((e.classname ?? '').toLowerCase())) names.add(e.name || 'default');
  return [...names];
}

export interface SpawnPoint {
  origin: Vec3;
  angles: Vec3;
}

/** The player's team: Axis (0) or Allies (1). */
export type Team = 0 | 1;
export const TEAM_NAMES = ['Axis', 'Allies'] as const;

const ALL_TEAMS = 3;

export type TimerKind = 'start' | 'stop' | 'checkpoint';

export interface GameHooks {
  /** The player's team (team only triggers, allowteams). */
  readonly team: Team;
  teleport(origin: Vec3, angles: Vec3, spawnflags: number): void;
  push(velocity: Vec3): void;
  damage(amount: number): void;
  timer(kind: TimerKind, run: string, opts: { speedLimit: number; checkpoint: number }): void;
  /** A mover was used by a target chain (Use_BinaryMover / Use_Func_Rotate). */
  useMover(entityNum: number): void;
  /** The trigger volume spawned for a door or plat was touched. */
  moverTrigger(entityNum: number): void;
  /** A target_speaker was used (sound only). */
  speaker(entityNum: number): void;
  /** +activate on a door or button (G_TryDoor / Use_BinaryMover when it stands still). */
  activateMover(entityNum: number): void;
  /** target_print: a message for the player. */
  print(text: string): void;
}

/** How far +activate reaches from the eye (Cmd_Activate_f). */
const ACTIVATE_RANGE = 96;
const ACTIVATE_MASK = CONTENTS_SOLID | CONTENTS_MISSILECLIP | CONTENTS_BODY | CONTENTS_CORPSE;

/** func_invisible_user: a brush the player uses (+activate) to fire its targets. */
interface Usable {
  entityNum: number;
  ent: Entity;
  model: number;
  origin: Vec3;
  /** ms before it can be used again ("delay"). */
  delay: number;
  /** allowteams bits (0 = every team). */
  teams: number;
}

interface Trigger {
  entityNum: number;
  ent: Entity;
  model: number; // inline model or -1 for box triggers
  origin: Vec3;
  absmin: Vec3;
  absmax: Vec3;
  wait: number; // ms between activations
  /** Teams that fire it (bits 1 << team). */
  teams: number;
  touch: (time: number, hooks: GameHooks) => void;
}

export class MapLogic {
  readonly entities: Entity[];
  readonly worldspawn: Entity;
  readonly rules: MapRules;
  /** Where each team appears; a team without spawns of its own gets the deathmatch spawns. */
  readonly teamSpawns: [SpawnPoint[], SpawnPoint[]] = [[], []];
  /** Teams a player can join: the ones with spawn points (both on maps without team spawns). */
  readonly teams: Team[];
  /** Entities the map script hides at the start (setstate invisible): no collision, triggers or drawing. */
  readonly hidden: Set<number>;
  readonly runs = new Map<string, { checkpoints: number }>();
  readonly moverDefs: MoverDef[] = [];
  private triggers: Trigger[] = [];
  private usables: Usable[] = [];
  private byTargetname = new Map<string, number[]>();
  private checkpointIndex = new Map<number, number>();
  private useDepth = 0;

  /** @param script the map script (maps/<name>.script), for the spawn objective it sets */
  constructor(
    entityString: string,
    private readonly world: PhysicsWorld,
    script?: string,
  ) {
    this.entities = parseEntities(entityString);
    this.worldspawn = this.entities.find((e) => e.classname === 'worldspawn') ?? {};
    const ws = this.worldspawn;
    this.rules = {
      ...DEFAULT_RULES,
      noJumpDelay: entityNum(ws, 'nojumpdelay') !== 0,
      noFallDamage: Math.max(0, Math.min(2, entityNum(ws, 'nofalldamage'))) as 0 | 1 | 2,
      noOverbounce: entityNum(ws, 'nooverbounce') !== 0,
      noWallbug: entityNum(ws, 'nowallbug') !== 0,
    };
    this.entities.forEach((e, i) => {
      if (e.targetname) {
        const list = this.byTargetname.get(e.targetname) ?? [];
        list.push(i);
        this.byTargetname.set(e.targetname, list);
      }
    });
    // what the map script hides at the start does not exist for the player
    this.hidden = hiddenEntities(this.entities, script);
    this.entities.forEach((e, i) => {
      if (!this.hidden.has(i)) this.setup(e, i);
    });
    this.collectSpawns(script);
    const withSpawns = ([0, 1] as const).filter((t) => this.teamSpawns[t].length);
    this.teams = withSpawns.length ? withSpawns : [0, 1];
    for (const t of [0, 1] as const) if (!this.teamSpawns[t].length) this.teamSpawns[t] = this.fallbackSpawns();
  }

  /** The team of a player that did not pick one: Axis, or Allies on maps with only Allied spawns. */
  get defaultTeam(): Team {
    return this.teams[0];
  }

  /** Where a player of the team appears. */
  spawn(team: Team): SpawnPoint {
    return this.teamSpawns[team][0];
  }

  /** SP_worldspawn copies the "gravity" key into g_gravity. */
  get gravity(): number {
    return entityNum(this.worldspawn, 'gravity', 800);
  }

  /**
   * Where the players of each team appear (SelectCTFSpawnPoint): the team's
   * spawns active at the start (spawnflags 2) closest to the team's spawn
   * objective, the team_WOLF_objective a map script's game_manager names
   * with setautospawn (the first one by default).
   */
  private collectSpawns(script: string | undefined): void {
    const isObjective = (e: Entity) => (e.classname ?? '').toLowerCase() === 'team_wolf_objective';
    // level.spawntargets: the objectives in spawn order (objective_Register)
    const objectives = this.entities.filter(isObjective);
    const start = script ? scriptStart(script) : { autoSpawns: new Map<number, string>(), alerted: new Map<string, number>() };
    for (const [cls, team] of [
      ['team_ctf_redspawn', 0],
      ['team_ctf_bluespawn', 1],
    ] as const) {
      const all = this.entities.filter((e, i) => (e.classname ?? '').toLowerCase() === cls && !this.hidden.has(i));
      if (!all.length) continue;
      // STARTACTIVE (spawnflags 2), toggled by every alertentity of the spawn event (Use_Team_Spawnpoint)
      const active = all.filter((e) => ((entityNum(e, 'spawnflags') & 2) !== 0) !== ((start.alerted.get(e.targetname ?? '') ?? 0) % 2 === 1));
      let spot = active[0] ?? all[0];
      if (active.length > 1 && objectives.length) {
        // setautospawn: G_Find by message (case insensitive), the objective's index; the first objective by default
        const name = start.autoSpawns.get(team)?.toLowerCase();
        const found = name === undefined ? undefined : this.entities.find((e) => (isObjective(e) ? (e.description ?? 'WARNING: No objective description set') : e.message)?.toLowerCase() === name);
        const objective = found && isObjective(found) ? found : objectives[0];
        const target = entityVec(objective, 'origin');
        let best = Infinity;
        for (const e of active) {
          // spawn points stay where the map puts them: SP_team_CTF_*spawn sets think = DropToFloor without a nextthink
          const o = entityVec(e, 'origin');
          const d = Math.hypot(f(o[0] - target[0]), f(o[1] - target[1]), f(o[2] - target[2]));
          if (d < best) {
            best = d;
            spot = e;
          }
        }
      }
      const origin = entityVec(spot, 'origin');
      origin[2] += 9;
      this.teamSpawns[team].push({ origin, angles: entityAngles(spot) });
    }
  }

  /** Spawns without team spawns: info_player_deathmatch (ET's spectator spawn). */
  private fallbackSpawns(): SpawnPoint[] {
    const spawns: SpawnPoint[] = [];
    const order = ['info_player_deathmatch', 'info_player_start', 'info_player_intermission'];
    for (const cls of order) {
      for (const e of this.entities) {
        if ((e.classname ?? '').toLowerCase() !== cls) continue;
        const origin = entityVec(e, 'origin');
        origin[2] += 9; // SelectSpawnPoint lifts the player a bit
        spawns.push({ origin, angles: entityAngles(e) });
      }
      if (spawns.length) return spawns;
    }
    return [{ origin: [0, 0, 64], angles: [0, 0, 0] }];
  }

  private setup(e: Entity, entNum: number): void {
    const cls = (e.classname ?? '').toLowerCase();
    const model = inlineModel(e);
    const origin = e.origin ? entityVec(e, 'origin') : ([0, 0, 0] as Vec3);

    if (model > 0 && SOLID_CLASSES.has(cls)) {
      this.world.addSolid(entNum, model, origin);
      return;
    }
    if (model > 0 && MOVER_CLASSES[cls]) {
      const def = createMoverDef(e, entNum, this.world, this.entities, this.byTargetname, this.gravity);
      if (!def) return;
      this.moverDefs.push(def);
      this.world.addSolid(entNum, model, def.kind === 'door' || def.kind === 'plat' || def.kind === 'button' ? def.pos1 : def.kind === 'train' ? [0, 0, 0] : def.origin);
      this.addMoverTrigger(def);
      return;
    }

    // ETJump timerun entities number their checkpoints in spawn order
    if (cls === 'target_checkpoint' || cls === 'trigger_checkpoint' || cls === 'trigger_checkpoint_ext') {
      const run = this.runInfo(e);
      this.checkpointIndex.set(entNum, run.checkpoints++);
    } else if (cls.includes('starttimer') || cls.includes('stoptimer')) {
      this.runInfo(e);
    }

    switch (cls) {
      case 'trigger_multiple':
      case 'trigger_once': {
        const teams = this.multiTeams(e);
        if (teams) this.addTrigger(entNum, e, cls === 'trigger_once' ? Infinity : this.waitMs(e, 0.5), (time, hooks) => this.useTargets(e, time, hooks), teams);
        break;
      }
      case 'trigger_teleport':
        this.addTrigger(entNum, e, 0, (_time, hooks) => {
          const dest = this.findTarget(e.target);
          if (dest) hooks.teleport(entityVec(dest, 'origin'), entityAngles(dest), entityNum(e, 'spawnflags'));
        });
        break;
      case 'trigger_hurt':
        this.addTrigger(entNum, e, entityNum(e, 'spawnflags') & 16 ? 1000 : 100, (_time, hooks) => hooks.damage(entityNum(e, 'dmg', 5)));
        break;
      case 'trigger_push':
        this.addTrigger(entNum, e, 0, (_time, hooks) => {
          const v = this.pushVelocity(e, entNum);
          if (v) hooks.push(v);
        });
        break;
      case 'trigger_starttimer':
      case 'trigger_stoptimer':
      case 'trigger_checkpoint':
        this.addTrigger(entNum, e, 0, (_time, hooks) => this.fireTimer(e, entNum, hooks));
        break;
      case 'trigger_starttimer_ext':
      case 'trigger_stoptimer_ext':
      case 'trigger_checkpoint_ext':
        this.addBoxTrigger(entNum, e, origin, (_time, hooks) => this.fireTimer(e, entNum, hooks));
        break;
      case 'func_invisible_user':
        // STARTOFF ones wait for a map script to turn them on (scripts do not run here)
        if (model > 0 && this.world.cm.models[model] && !(entityNum(e, 'spawnflags') & 1)) {
          this.usables.push({ entityNum: entNum, ent: e, model, origin, delay: entityNum(e, 'delay') * 1000, teams: allowTeams(e) });
        }
        break;
    }
  }

  /**
   * Touch_Multi's filters for a human soldier: the teams (bits 1 << team) left
   * by AXIS_ONLY / ALLIED_ONLY, none for BOTONLY and the class-only flags.
   */
  private multiTeams(e: Entity): number {
    const flags = entityNum(e, 'spawnflags');
    // BOTONLY, FIELDOPS / MEDIC / ENGINEER / COVERTOPS only
    if (flags & (8 | 32 | 64 | 128 | 256)) return 0;
    return flags & 1 ? 1 : flags & 2 ? 2 : ALL_TEAMS;
  }

  private runInfo(e: Entity): { checkpoints: number } {
    const name = e.name || 'default';
    let run = this.runs.get(name);
    if (!run) {
      run = { checkpoints: 0 };
      this.runs.set(name, run);
    }
    return run;
  }

  private waitMs(e: Entity, fallback: number): number {
    const w = entityNum(e, 'wait', fallback);
    return w < 0 ? Infinity : w * 1000;
  }

  private addTrigger(entNum: number, ent: Entity, wait: number, touch: Trigger['touch'], teams = ALL_TEAMS): void {
    const model = inlineModel(ent);
    const m = this.world.cm.models[model];
    if (model <= 0 || !m) return;
    const origin = ent.origin ? entityVec(ent, 'origin') : ([0, 0, 0] as Vec3);
    this.triggers.push({
      entityNum: entNum,
      ent,
      model,
      origin,
      absmin: [m.mins[0] + origin[0], m.mins[1] + origin[1], m.mins[2] + origin[2]],
      absmax: [m.maxs[0] + origin[0], m.maxs[1] + origin[1], m.maxs[2] + origin[2]],
      wait,
      teams,
      touch,
    });
  }

  private addBoxTrigger(entNum: number, ent: Entity, origin: Vec3, touch: Trigger['touch']): void {
    const mins = entityVec(ent, 'mins');
    const maxs = entityVec(ent, 'maxs');
    this.triggers.push({
      entityNum: entNum,
      ent,
      model: -1,
      origin,
      absmin: [origin[0] + mins[0], origin[1] + mins[1], origin[2] + mins[2]],
      absmax: [origin[0] + maxs[0], origin[1] + maxs[1], origin[2] + maxs[2]],
      wait: 0,
      teams: ALL_TEAMS,
      touch,
    });
  }

  /**
   * Think_SpawnNewDoorTrigger (touch doors without a targetname: the door's
   * bounds grown 120 units along its thinnest axis) and SpawnPlatTrigger (a
   * thin volume just above the plat's low position).
   */
  private addMoverTrigger(d: MoverDef): void {
    const ent = this.entities[d.entityNum];
    const touch: Trigger['touch'] = (_time, hooks) => hooks.moverTrigger(d.entityNum);
    if (d.kind === 'door' && d.spawnflags & 8 && !d.hasTargetname && !d.shootable) {
      const mins: Vec3 = [0, 1, 2].map((i) => d.pos1[i] + d.mins[i] - 1) as Vec3;
      const maxs: Vec3 = [0, 1, 2].map((i) => d.pos1[i] + d.maxs[i] + 1) as Vec3;
      let best = 0;
      for (let i = 1; i < 3; i++) if (maxs[i] - mins[i] < maxs[best] - mins[best]) best = i;
      maxs[best] += 120;
      mins[best] -= 120;
      this.triggers.push({ entityNum: -1 - d.entityNum, ent, model: -1, origin: [0, 0, 0], absmin: mins, absmax: maxs, wait: 0, teams: ALL_TEAMS, touch });
    } else if (d.kind === 'plat' && !d.hasTargetname) {
      const mins: Vec3 = [d.pos1[0] + d.mins[0] + 33, d.pos1[1] + d.mins[1] + 33, d.pos1[2] + d.mins[2]];
      const maxs: Vec3 = [d.pos1[0] + d.maxs[0] - 33, d.pos1[1] + d.maxs[1] - 33, d.pos1[2] + d.maxs[2] + 8];
      for (let i = 0; i < 2; i++) {
        if (maxs[i] <= mins[i]) {
          mins[i] = d.pos1[i] + (d.mins[i] + d.maxs[i]) * 0.5;
          maxs[i] = mins[i] + 1;
        }
      }
      this.triggers.push({ entityNum: -1 - d.entityNum, ent, model: -1, origin: [0, 0, 0], absmin: mins, absmax: maxs, wait: 0, teams: ALL_TEAMS, touch });
    }
  }

  /** The movers of one player (each session owns its own state). */
  createMovers(): MoverSet {
    return new MoverSet(this.moverDefs, this.world, this.rules.svFrameMsec);
  }

  /** G_UseTargets for an entity (movers reaching their end, train path corners). */
  fireTargets(entNum: number, time: number, hooks: GameHooks): void {
    const e = this.entities[entNum];
    if (e) this.useTargets(e, time, hooks);
  }

  private fireTimer(e: Entity, entNum: number, hooks: GameHooks): void {
    const cls = (e.classname ?? '').toLowerCase();
    const kind: TimerKind = cls.includes('start') ? 'start' : cls.includes('stop') ? 'stop' : 'checkpoint';
    hooks.timer(kind, e.name || 'default', { speedLimit: entityNum(e, 'speed_limit', 700), checkpoint: this.checkpointIndex.get(entNum) ?? -1 });
  }

  private findTarget(name: string | undefined): Entity | undefined {
    if (!name) return undefined;
    const list = this.byTargetname.get(name);
    return list ? this.entities[list[0]] : undefined;
  }

  /** Q3 AimAtTarget: velocity that lands the player on the target's apex. */
  private pushVelocity(e: Entity, entNum: number): Vec3 | null {
    const dest = this.findTarget(e.target);
    const trig = this.triggers.find((t) => t.entityNum === entNum);
    if (!dest || !trig) return null;
    const origin: Vec3 = [(trig.absmin[0] + trig.absmax[0]) * 0.5, (trig.absmin[1] + trig.absmax[1]) * 0.5, (trig.absmin[2] + trig.absmax[2]) * 0.5];
    const target = entityVec(dest, 'origin');
    const height = target[2] - origin[2];
    const gravity = this.gravity;
    const time = Math.sqrt(Math.max(0, height / (0.5 * gravity)));
    if (!time) return null;
    const dir: Vec3 = [target[0] - origin[0], target[1] - origin[1], 0];
    const dist = Math.hypot(dir[0], dir[1]);
    const forward = dist / time;
    return dist ? [(dir[0] / dist) * forward, (dir[1] / dist) * forward, time * gravity] : [0, 0, time * gravity];
  }

  /** G_UseTargets: fire every entity whose targetname matches e.target. */
  private useTargets(e: Entity, time: number, hooks: GameHooks): void {
    if (!e.target || this.useDepth > 8) return;
    this.useDepth++;
    for (const idx of this.byTargetname.get(e.target) ?? []) this.use(this.entities[idx], idx, time, hooks);
    this.useDepth--;
  }

  private use(e: Entity, entNum: number, time: number, hooks: GameHooks): void {
    // G_UseEntity: allowteams
    const teams = allowTeams(e);
    if (teams && !(teams & (1 << hooks.team))) return;
    const cls = (e.classname ?? '').toLowerCase();
    switch (cls) {
      case 'target_starttimer':
      case 'target_stoptimer':
      case 'target_checkpoint':
        this.fireTimer(e, entNum, hooks);
        break;
      case 'target_teleporter': {
        const dest = this.findTarget(e.target);
        if (dest) hooks.teleport(entityVec(dest, 'origin'), entityAngles(dest), entityNum(e, 'spawnflags'));
        break;
      }
      case 'target_speaker':
        hooks.speaker(entNum);
        break;
      case 'target_kill':
        hooks.damage(100000);
        break;
      case 'target_print':
        if (e.message) hooks.print(e.message);
        break;
      case 'target_relay':
      case 'target_delay':
        this.useTargets(e, time, hooks);
        break;
      default:
        if (MOVER_CLASSES[cls]) hooks.useMover(entNum);
    }
  }

  /**
   * Cmd_Activate_f: what the player looks at within 96 units of the eye. A
   * door or button hit first is used; otherwise the nearest func_invisible_user
   * before the wall (the second trace with CONTENTS_TRIGGER).
   * @param cooldowns per-player entity -> next allowed use (invisible users' "delay")
   */
  activate(eye: Vec3, forward: Vec3, time: number, hooks: GameHooks, cooldowns: Map<number, number>): void {
    const end: Vec3 = [eye[0] + forward[0] * ACTIVATE_RANGE, eye[1] + forward[1] * ACTIVATE_RANGE, eye[2] + forward[2] * ACTIVATE_RANGE];
    const zero: Vec3 = [0, 0, 0];
    const tr = this.world.trace(eye, zero, zero, end, ACTIVATE_MASK);
    const mover = this.moverDefs.find((d) => d.entityNum === tr.entityNum);
    if (mover && (mover.kind === 'door' || mover.kind === 'doorRotating' || mover.kind === 'button')) {
      hooks.activateMover(mover.entityNum);
      return;
    }
    let best: Usable | null = null;
    let bestFraction = tr.fraction;
    for (const u of this.usables) {
      const t = this.world.cm.boxTrace(eye, end, zero, zero, -1, u.model, u.origin);
      const f = t.startsolid ? 0 : t.fraction;
      if (f < 1 && f <= bestFraction) {
        best = u;
        bestFraction = f;
      }
    }
    // G_UseEntity: allowteams
    if (!best || (best.teams && !(best.teams & (1 << hooks.team)))) return;
    // use_invisible_user: "delay" between uses
    const key = -100000 - best.entityNum;
    if (time < (cooldowns.get(key) ?? -Infinity)) return;
    cooldowns.set(key, time + best.delay);
    this.useTargets(best.ent, time, hooks);
  }

  /**
   * G_TouchTriggers: fire triggers overlapping the player's box.
   * @param cooldowns per-player trigger entity -> next allowed activation time
   */
  touchTriggers(origin: Vec3, mins: Vec3, maxs: Vec3, time: number, hooks: GameHooks, cooldowns: Map<number, number>): void {
    const amin: Vec3 = [origin[0] + mins[0], origin[1] + mins[1], origin[2] + mins[2]];
    const amax: Vec3 = [origin[0] + maxs[0], origin[1] + maxs[1], origin[2] + maxs[2]];
    for (const t of this.triggers) {
      if (amin[0] > t.absmax[0] || amin[1] > t.absmax[1] || amin[2] > t.absmax[2] || amax[0] < t.absmin[0] || amax[1] < t.absmin[1] || amax[2] < t.absmin[2]) continue;
      if (!(t.teams & (1 << hooks.team))) continue;
      if (t.model > 0 && !this.world.boxTouchesModel(origin, mins, maxs, t.model, t.origin)) continue;
      if (time < (cooldowns.get(t.entityNum) ?? -Infinity)) continue;
      cooldowns.set(t.entityNum, time + t.wait);
      t.touch(time, hooks);
    }
  }

}

